// client/src/pages/AccountAnalytics.js
//
// User-facing analytics page (Business Wizard tier and above).
//
// CHANGES IN THIS REVISION (Shareable Links & Reactions build):
//   - Added a Reactions stat card to the top-of-page summary row.
//   - Activity chart now plots reactions alongside views & clicks.
//   - All Cards tab: each card row shows aggregate reaction count;
//     expanded detail shows per-emoji breakdown + per-recipient
//     reaction counts.
//   - NEW: "Shareable Links" tab — by-label view of every link the
//     user has generated. Shows per-link views, unique-IP estimate,
//     and per-emoji reactions. Powered by GET /analytics/shareable-links
//     and getShareableLinkAnalytics in analyticsService.
//   - Tab persistence via URL hash (#email, #cards, #activity, #links)
//     so a user who lives in the Shareable Links view doesn't get
//     reset every visit.
//   - Reaction-friendly empty states everywhere.
//
// EXISTING FEATURES PRESERVED:
//   - Period selector (7/30/90/all)
//   - Unopened alert
//   - Top-level stat cards
//   - Activity chart
//   - Overview tab (template performance, device breakdown, profile links)
//   - All Cards tab (with accordion expand)
//   - Activity tab
//
// CHANGES IN THIS REVISION (Dashboard build):
//   - Added "Dashboard" back button at top of page.

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import api from '../services/api.js';
import { useAuth } from '../hooks/useAuth.js';

// Shared card class
const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-xl';

// ─── Helpers ──────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '—';
  const diffMs = new Date() - new Date(dateStr);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

// ─── Stat Card ────────────────────────────────────────────
function StatCard({ label, value, subtext, color = 'text-gold', icon }) {
  return (
    <div className={`${cardClass} p-4 sm:p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          <p className="text-royal-100 text-sm mt-1">{label}</p>
          {subtext && <p className="text-royal-100 text-xs mt-0.5">{subtext}</p>}
        </div>
        {icon && <div className="text-2xl opacity-60">{icon}</div>}
      </div>
    </div>
  );
}

// ─── Chart Tooltip ────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-royal-900 border border-royal-600 rounded-lg px-3 py-2 shadow-xl">
      <p className="text-royal-100 text-xs mb-1">{formatDateShort(label)}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: <span className="font-semibold">{entry.value}</span>
        </p>
      ))}
    </div>
  );
}

// ─── Device Icon ──────────────────────────────────────────
function DeviceIcon({ type }) {
  if (type === 'Mobile') return <span title="Mobile">📱</span>;
  if (type === 'Tablet') return <span title="Tablet">📲</span>;
  return <span title="Desktop">🖥️</span>;
}

// ─── Event Icon ───────────────────────────────────────────
function EventIcon({ type }) {
  if (type === 'view') {
    return (
      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </div>
    );
  }
  if (type === 'click') {
    return (
      <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
        </svg>
      </div>
    );
  }
  if (type === 'send') {
    return (
      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      </div>
    );
  }
  if (type === 'reaction') {
    return (
      <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
        <span className="text-base">💬</span>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-royal-600/50 flex items-center justify-center flex-shrink-0">
      <svg className="w-4 h-4 text-royal-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
}

// ─── Reactions Pill List ──────────────────────────────────
// Compact horizontal list of reaction emojis with counts.
// Used in card rows and expanded recipient details.
function ReactionsPills({ reactions }) {
  if (!reactions || reactions.length === 0) return null;
  const filtered = reactions.filter(r => (r.count || 0) > 0);
  if (filtered.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-2">
      {filtered.map(r => (
        <span key={r.emojiid} className="inline-flex items-center gap-0.5 text-sm">
          <span aria-hidden="true">{r.emoji_char}</span>
          <span className="text-royal-100 font-medium">{r.count}</span>
        </span>
      ))}
    </span>
  );
}

// ─── Card Row (Accordion) ─────────────────────────────────
function CardRow({ card, isExpanded, onToggle }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const viewRate = card.recipient_count > 0
    ? Math.round((parseInt(card.recipients_viewed) / parseInt(card.recipient_count)) * 100)
    : 0;

  // Card row reaction summary — total only, server provides this.
  // The per-emoji breakdown lives in the expanded detail.
  const reactionTotal = parseInt(card.reaction_count) || 0;

  const handleToggle = async () => {
    if (!isExpanded && !detail) {
      setLoadingDetail(true);
      try {
        const data = await api.get(`/analytics/cards/${card.moonbeamid}`);
        setDetail(data);
      } catch (err) {
        console.error('Load card detail error:', err);
      }
      setLoadingDetail(false);
    }
    onToggle();
  };

  // Aggregate reaction breakdown for the expanded view — server returns
  // recipients with their own reactions[] field; we also surface the
  // card-level aggregate from detail.reactionTotals if the server
  // includes it. Falls back to summing per-recipient counts.
  const cardReactionBreakdown = (() => {
    if (!detail) return [];
    if (detail.reactionTotals && Array.isArray(detail.reactionTotals)) {
      return detail.reactionTotals;
    }
    // Sum per-recipient reactions, anonymized
    const agg = new Map();
    for (const r of detail.recipients || []) {
      for (const rx of (r.reactions || [])) {
        const cur = agg.get(rx.emojiid) || { emojiid: rx.emojiid, emoji_char: rx.emoji_char, count: 0 };
        cur.count += parseInt(rx.count) || 0;
        agg.set(rx.emojiid, cur);
      }
    }
    return Array.from(agg.values());
  })();

  return (
    <div className="border border-royal-700 dark:border-royal-700/40 rounded-xl overflow-hidden bg-royal-900 dark:bg-royal-800/20">
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-royal-800/30 transition-colors text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="text-white text-sm sm:text-base font-medium truncate">{card.templatename}</p>
            {parseInt(card.recipients_unopened) > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-900/40 text-amber-400 text-xs rounded-full flex-shrink-0">
                {card.recipients_unopened} unopened
              </span>
            )}
          </div>
          <p className="text-royal-100 text-sm truncate">
            {card.recipientname || `${card.recipient_count} recipient${parseInt(card.recipient_count) !== 1 ? 's' : ''}`}
            {' · '}{formatDate(card.sentat)}
          </p>
        </div>

        <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-blue-400 text-sm font-semibold">{card.total_views} views</p>
            <p className="text-royal-100 text-xs">
              {viewRate}% opened
              {reactionTotal > 0 && (
                <> · <span className="text-pink-400">💬 {reactionTotal}</span></>
              )}
            </p>
          </div>
          <div className="text-right sm:hidden">
            <p className="text-blue-400 text-sm font-semibold">{card.total_views}</p>
            {reactionTotal > 0 && (
              <p className="text-pink-400 text-xs">💬 {reactionTotal}</p>
            )}
          </div>
          <svg className={`w-5 h-5 text-royal-100 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-royal-700/40 px-4 py-4">
          {loadingDetail ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {/* Card stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-royal-900/40 rounded-lg p-3 text-center">
                  <p className="text-blue-400 text-xl font-bold">{detail.recipients?.length || 0}</p>
                  <p className="text-royal-100 text-xs">Recipients</p>
                </div>
                <div className="bg-royal-900/40 rounded-lg p-3 text-center">
                  <p className="text-green-400 text-xl font-bold">{detail.recipients?.filter(r => r.viewcount > 0).length || 0}</p>
                  <p className="text-royal-100 text-xs">Opened</p>
                </div>
                <div className="bg-royal-900/40 rounded-lg p-3 text-center">
                  <p className="text-gold text-xl font-bold">{detail.recipients?.reduce((s, r) => s + (r.viewcount || 0), 0) || 0}</p>
                  <p className="text-royal-100 text-xs">Total Views</p>
                </div>
                <div className="bg-royal-900/40 rounded-lg p-3 text-center">
                  <p className="text-pink-400 text-xl font-bold">{reactionTotal}</p>
                  <p className="text-royal-100 text-xs">Reactions</p>
                </div>
              </div>

              {/* Per-emoji reaction breakdown */}
              {cardReactionBreakdown.length > 0 && (
                <div className="bg-royal-900/40 rounded-lg p-3">
                  <p className="text-royal-100 text-xs font-semibold uppercase tracking-wider mb-2">Reactions Breakdown</p>
                  <div className="flex flex-wrap gap-3">
                    {cardReactionBreakdown.map(r => (
                      <div key={r.emojiid} className="flex items-center gap-1.5 px-2 py-1 bg-royal-800/40 rounded-md">
                        <span className="text-base" aria-hidden="true">{r.emoji_char}</span>
                        <span className="text-white text-sm font-medium">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Device breakdown */}
              {detail.devices?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {detail.devices.map((d) => (
                    <div key={d.devicetype} className="flex items-center gap-1.5 bg-royal-900/40 rounded-full px-3 py-1.5 text-sm">
                      <DeviceIcon type={d.devicetype} />
                      <span className="text-royal-100">{d.devicetype}</span>
                      <span className="text-gold font-semibold">{d.count}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Recipients list */}
              <div>
                <p className="text-royal-100 text-sm font-semibold uppercase tracking-wider mb-2">Recipients</p>
                <div className="bg-royal-900/30 rounded-lg overflow-hidden divide-y divide-royal-800">
                  {(detail.recipients || []).map((r) => (
                    <RecipientDetail key={r.recipientid} recipient={r} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-royal-100 text-sm text-center py-4">Unable to load details</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Recipient Detail Row ─────────────────────────────────
function RecipientDetail({ recipient }) {
  const [expanded, setExpanded] = useState(false);
  const name = [recipient.firstname, recipient.lastname].filter(Boolean).join(' ') || recipient.recipientname || recipient.email;
  const viewed = recipient.viewcount > 0;

  // Per-recipient reaction summary (from getRecipientAnalytics)
  const reactions = recipient.reactions || [];
  const hasReactions = reactions.some(r => (r.count || 0) > 0);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between py-2.5 px-3 hover:bg-royal-800/30 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${viewed ? 'bg-green-400' : 'bg-royal-600'}`} />
          <div className="min-w-0">
            <p className="text-white text-sm truncate">{name}</p>
            <p className="text-royal-100 text-xs truncate">
              {recipient.email}
              {hasReactions && <span className="ml-1.5"><ReactionsPills reactions={reactions} /></span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {viewed ? (
            <div className="text-right">
              <p className="text-green-400 text-sm font-semibold">{recipient.viewcount} view{recipient.viewcount !== 1 ? 's' : ''}</p>
              <p className="text-royal-100 text-xs">{formatDateShort(recipient.firstviewedat)}</p>
            </div>
          ) : (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              recipient.emailstatus === 'sent' ? 'bg-amber-900/40 text-amber-400' :
              recipient.emailstatus === 'failed' ? 'bg-red-900/40 text-red-400' :
              'bg-royal-700 text-royal-100'
            }`}>
              {recipient.emailstatus === 'sent' ? 'Not opened' : recipient.emailstatus}
            </span>
          )}
          {recipient.view_details?.length > 0 && (
            <svg className={`w-4 h-4 text-royal-100 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          )}
        </div>
      </button>

      {expanded && recipient.view_details?.length > 0 && (
        <div className="px-3 pb-3 pl-8 space-y-1.5">
          <p className="text-royal-100 text-xs font-semibold uppercase tracking-wider">View History</p>
          {recipient.view_details.map((view, i) => (
            <div key={i} className="flex items-center gap-2 text-xs bg-royal-900/40 rounded px-2.5 py-1.5">
              <DeviceIcon type={view.devicetype} />
              <span className="text-royal-100">{formatDateTime(view.viewedat)}</span>
              <span className="text-royal-100">{view.browser}{view.os ? ` · ${view.os}` : ''}</span>
              {view.city && <span className="text-royal-100">· {view.city}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shareable Link Row (analytics view) ──────────────────
// Different from the ShareableLinkRow on MyMoonbeams — this is read-only,
// shows fuller stats (unique visitors, per-emoji reactions), and links
// back to the source card.
function ShareableLinkAnalyticsRow({ link }) {
  const viewsInRange = parseInt(link.views_in_range) || 0;
  const uniqueVisitors = parseInt(link.unique_visitors) || 0;
  const reactionTotal = parseInt(link.reaction_total) || 0;
  const reactions = Array.isArray(link.reactions) ? link.reactions : [];
  const isActive = link.isactive !== false;

  // Build the share URL client-side. The analytics service returns
  // `linktoken` and `moonbeam_uuid` (the parent card's uuid), but not a
  // pre-constructed URL — different from the MyMoonbeams shareable-link
  // endpoint, which does include `shareUrl`. Using the current origin
  // means it works correctly in production, staging, and local dev
  // without env wiring on the front end.
  const shareUrl = (link.linktoken && link.moonbeam_uuid && typeof window !== 'undefined')
    ? `${window.location.origin}/card/${link.moonbeam_uuid}?l=${link.linktoken}`
    : null;

  return (
    <div className={`border border-royal-700 dark:border-royal-700/40 rounded-xl p-4 ${!isActive ? 'opacity-75' : ''} bg-royal-900 dark:bg-royal-800/20`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span aria-hidden="true">📌</span>
            <p className="text-white text-base font-medium truncate">"{link.label}"</p>
            {!isActive && (
              <span className="px-1.5 py-0.5 bg-amber-900/40 text-amber-400 text-xs rounded-full flex-shrink-0">
                Disabled
              </span>
            )}
          </div>
          <p className="text-royal-100 text-sm mt-0.5">
            from "{link.templatename}" · created {formatDate(link.createdat)}
          </p>
        </div>

        {/* View source card button — uses locally-computed shareUrl */}
        {shareUrl && (
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-gold hover:text-gold-light flex-shrink-0 transition-colors"
            title="Open share URL"
          >
            View →
          </a>
        )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div className="bg-royal-900/40 rounded-lg p-3 text-center">
          <p className="text-blue-400 text-xl font-bold">{viewsInRange}</p>
          <p className="text-royal-100 text-xs">Views</p>
        </div>
        <div className="bg-royal-900/40 rounded-lg p-3 text-center">
          <p className="text-gold text-xl font-bold">~{uniqueVisitors}</p>
          <p className="text-royal-100 text-xs">Visitors*</p>
        </div>
        <div className="bg-royal-900/40 rounded-lg p-3 text-center">
          <p className="text-pink-400 text-xl font-bold">{reactionTotal}</p>
          <p className="text-royal-100 text-xs">Reactions</p>
        </div>
      </div>

      {/* Per-emoji reaction breakdown */}
      {reactions.length > 0 && reactionTotal > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {reactions.map(r => (
            <div key={r.emojiid} className="flex items-center gap-1.5 px-2.5 py-1 bg-royal-900/40 rounded-md">
              <span className="text-base" aria-hidden="true">{r.emoji_char}</span>
              <span className="text-white text-sm font-medium">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────
function SimplePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="text-sm text-royal-600 dark:text-royal-100 hover:text-royal-900 dark:hover:text-gold disabled:opacity-40 transition-colors"
      >
        ← Prev
      </button>
      <span className="text-royal-600 dark:text-royal-100 text-sm">Page {page} of {totalPages}</span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="text-sm text-royal-600 dark:text-royal-100 hover:text-royal-900 dark:hover:text-gold disabled:opacity-40 transition-colors"
      >
        Next →
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────
const VALID_TABS = ['overview', 'cards', 'activity', 'links'];

export default function AccountAnalytics() {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [cardsData, setCardsData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [linksData, setLinksData] = useState(null);

  // Shareable links analytics — separate from profile links above.
  // Keyed by [shareable] to avoid colliding with the existing 'links'
  // concept (which is profile/CTA links).
  const [shareableLinksData, setShareableLinksData] = useState(null);
  const [shareableLinksLoading, setShareableLinksLoading] = useState(false);
  const [shareableLinkFilter, setShareableLinkFilter] = useState('all'); // 'all' | 'active' | 'disabled'
  const [shareableLinkSort, setShareableLinkSort] = useState('recent');  // 'recent' | 'views' | 'label'

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  // Read initial tab from URL hash so deep-linking and tab persistence work
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'overview';
    const hash = window.location.hash.replace('#', '');
    return VALID_TABS.includes(hash) ? hash : 'overview';
  });

  // Persist tab to URL hash on change
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${activeTab}`) {
      window.history.replaceState({}, '', `#${activeTab}`);
    }
  }, [activeTab]);

  // Cards tab state
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsSort, setCardsSort] = useState('newest');
  const [cardsFilter, setCardsFilter] = useState('all');
  const [expandedCard, setExpandedCard] = useState(null);

  // Activity tab state
  const [activityPage, setActivityPage] = useState(1);

  const canUseLinks = user?.tier?.canuselinks;

  const periodLabel = period === 7 ? 'last 7 days' : period === 30 ? 'last 30 days' : period === 90 ? 'last 90 days' : 'all time';

  // Fetch dashboard summary
  const fetchDashboard = useCallback(async () => {
    try {
      const result = await api.get(`/analytics/dashboard?days=${period}`);
      setDashboardData(result);
    } catch (err) {
      console.error('Fetch dashboard error:', err);
    }
  }, [period]);

  // Fetch cards list
  const fetchCards = useCallback(async () => {
    try {
      const result = await api.get(`/analytics/cards?page=${cardsPage}&sort=${cardsSort}&filter=${cardsFilter}&days=${period}`);
      setCardsData(result);
    } catch (err) {
      console.error('Fetch cards error:', err);
    }
  }, [cardsPage, cardsSort, cardsFilter, period]);

  // Fetch activity feed
  const fetchActivity = useCallback(async () => {
    try {
      const result = await api.get(`/analytics/activity?page=${activityPage}&days=${period}`);
      setActivityData(result);
    } catch (err) {
      console.error('Fetch activity error:', err);
    }
  }, [activityPage, period]);

  // Fetch link stats (profile/CTA links)
  const fetchLinks = useCallback(async () => {
    if (!canUseLinks) return;
    try {
      const result = await api.get('/analytics/links');
      setLinksData(result);
    } catch (err) {
      console.error('Fetch links error:', err);
    }
  }, [canUseLinks]);

  // Fetch shareable-link analytics (new — by-label view).
  // Lazy: only when the user enters the tab, to avoid the cost on every
  // analytics page load. Refetches if period changes while on the tab.
  const fetchShareableLinks = useCallback(async () => {
    setShareableLinksLoading(true);
    try {
      const result = await api.get(`/analytics/shareable-links?days=${period}`);
      setShareableLinksData(result);
    } catch (err) {
      console.error('Fetch shareable links error:', err);
      setShareableLinksData({ links: [] });
    } finally {
      setShareableLinksLoading(false);
    }
  }, [period]);

  // Initial load — fetch everything except shareable links (lazy)
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchDashboard(), fetchCards(), fetchActivity(), fetchLinks()])
      .finally(() => setLoading(false));
  }, [fetchDashboard, fetchCards, fetchActivity, fetchLinks]);

  // Refetch cards/activity on dep changes
  useEffect(() => { fetchCards(); }, [fetchCards]);
  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  // Lazy-fetch shareable link analytics when the user enters the tab,
  // or when the period changes while they're on it.
  useEffect(() => {
    if (activeTab !== 'links') return;
    fetchShareableLinks();
  }, [activeTab, fetchShareableLinks]);

  // Reset pages when period changes
  useEffect(() => {
    setCardsPage(1);
    setActivityPage(1);
    setExpandedCard(null);
  }, [period, cardsSort, cardsFilter]);

  const summary = dashboardData?.summary || {};
  const dailyBreakdown = dashboardData?.dailyBreakdown || [];
  const deviceBreakdown = dashboardData?.deviceBreakdown || [];
  const templatePerformance = dashboardData?.templatePerformance || [];
  const reactionsByEmoji = dashboardData?.reactionsByEmoji || []; // NEW
  const cards = cardsData?.cards || [];
  const cardsPagination = cardsData?.pagination || {};
  const activity = activityData?.activity || [];
  const activityPagination = activityData?.pagination || {};
  const links = linksData?.links || [];

  // ─── Derive shareable link tab data ─────────────────────────────────
  const shareableLinksAll = shareableLinksData?.links || [];

  const shareableLinksFiltered = shareableLinksAll.filter(link => {
    if (shareableLinkFilter === 'active') return link.isactive !== false;
    if (shareableLinkFilter === 'disabled') return link.isactive === false;
    return true;
  });

  const shareableLinksSorted = [...shareableLinksFiltered].sort((a, b) => {
    if (shareableLinkSort === 'views') {
      return (parseInt(b.views_in_range) || 0) - (parseInt(a.views_in_range) || 0);
    }
    if (shareableLinkSort === 'label') {
      return (a.label || '').localeCompare(b.label || '');
    }
    // 'recent' — by last activity, falling back to createdat
    const aTime = new Date(a.lastviewedat || a.createdat).getTime();
    const bTime = new Date(b.lastviewedat || b.createdat).getTime();
    return bTime - aTime;
  });

  // Aggregate stats across all shareable links for the stat-card row at
  // the top of the Shareable Links tab.
  const shareableLinksAggregate = {
    totalLinks: shareableLinksAll.length,
    activeLinks: shareableLinksAll.filter(l => l.isactive !== false).length,
    disabledLinks: shareableLinksAll.filter(l => l.isactive === false).length,
    totalViews: shareableLinksAll.reduce((s, l) => s + (parseInt(l.views_in_range) || 0), 0),
    totalUniqueVisitors: shareableLinksAll.reduce((s, l) => s + (parseInt(l.unique_visitors) || 0), 0),
    totalReactions: shareableLinksAll.reduce((s, l) => s + (parseInt(l.reaction_total) || 0), 0),
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* Dashboard Back Link */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl text-gold-dark dark:text-gold">Analytics</h1>
            <p className="text-royal-600 dark:text-royal-100 text-sm mt-1">Track how your eCards are performing</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <select
              value={period}
              onChange={(e) => setPeriod(parseInt(e.target.value))}
              className="px-4 py-2.5 bg-royal-900 dark:bg-royal-800/60 border border-royal-700 dark:border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={0}>All time</option>
            </select>
            <p className="text-royal-500 dark:text-royal-100 text-xs italic">Applies to all data on this page</p>
          </div>
        </div>

        {/* Unopened Alert */}
        {summary.unopenedCount > 0 && (
          <div className="mb-6 p-4 bg-amber-100 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-600/30 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📬</span>
              <div>
                <p className="text-amber-800 dark:text-amber-200 font-medium text-sm">
                  {summary.unopenedCount} recipient{summary.unopenedCount !== 1 ? 's' : ''} haven't opened their eCard yet
                </p>
                <p className="text-amber-700 dark:text-amber-200/70 text-xs">
                  Across {summary.cardsWithUnopened} card{summary.cardsWithUnopened !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => { setActiveTab('cards'); setCardsFilter('unopened'); }}
              className="text-sm text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 underline transition-colors"
            >
              View unopened →
            </button>
          </div>
        )}

        {/* Summary Stats — 5 cards now (added Reactions) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mb-6">
          <StatCard label="Cards Sent" value={summary.totalCardsSent || 0} color="text-green-400" icon="📤" />
          <StatCard label="Total Views" value={summary.totalViews || 0} color="text-blue-400" icon="👁️" />
          <StatCard label="View Rate" value={`${summary.viewRate || 0}%`} subtext={`${summary.cardsViewed || 0} cards opened`} color="text-gold" icon="📊" />
          <StatCard label="Link Clicks" value={summary.totalClicks || 0} color="text-pink-400" icon="🔗" />
          <StatCard label="Reactions" value={summary.totalReactions || 0} color="text-pink-400" icon="💬" />
        </div>

        {/* Daily Chart — now plots reactions alongside views & clicks */}
        {dailyBreakdown.length > 0 && (
          <div className={`${cardClass} p-4 sm:p-5 mb-6`}>
            <h2 className="text-white text-base font-semibold mb-4">Activity Over Time</h2>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyBreakdown} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#60a5fa" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#60a5fa" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="clicksGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d4af37" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#d4af37" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="reactionsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tickFormatter={formatDateShort} tick={{ fill: '#9fa8da', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#9fa8da', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#60a5fa" fill="url(#viewsGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#d4af37" fill="url(#clicksGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="reactions" name="Reactions" stroke="#ec4899" fill="url(#reactionsGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-royal-100 text-xs mt-3 text-center italic">
              Showing {periodLabel} — adjust the time range using the selector in the top right
            </p>
          </div>
        )}

        {/* Tabs — added Shareable Links */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'cards', label: `All Cards (${cardsPagination.total || 0})` },
            { key: 'links', label: 'Shareable Links' },
            { key: 'activity', label: 'Activity' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-gold text-royal-900 shadow-md'
                  : 'bg-royal-900 dark:bg-royal-800/50 text-royal-100 border border-royal-700 dark:border-royal-600 hover:border-gold/50 hover:text-gold'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ─── OVERVIEW TAB ──────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Reactions breakdown (NEW) — only shown when we have any */}
            {reactionsByEmoji.length > 0 && reactionsByEmoji.some(r => (r.count || 0) > 0) && (
              <div className={`${cardClass} p-5`}>
                <h2 className="text-white text-base font-semibold mb-4">Reactions Breakdown</h2>
                <div className="flex flex-wrap gap-3">
                  {reactionsByEmoji.filter(r => (r.count || 0) > 0).map(r => (
                    <div key={r.emojiid} className="flex items-center gap-2 px-3 py-2 bg-royal-900/40 rounded-lg">
                      <span className="text-2xl" aria-hidden="true">{r.emoji_char}</span>
                      <div>
                        <p className="text-white font-bold text-lg leading-tight">{r.count}</p>
                        <p className="text-royal-100 text-xs leading-tight">{r.display_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-royal-100 text-xs mt-3 italic">
                  Reactions from email recipients in the {periodLabel}. Shareable-link reactions appear in their own tab.
                </p>
              </div>
            )}

            {/* Template Performance */}
            {templatePerformance.length > 0 && (
              <div className={`${cardClass} p-5`}>
                <h2 className="text-white text-base font-semibold mb-4">Template Performance</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-royal-700">
                        <th className="text-left text-royal-100 text-xs uppercase tracking-wider py-2 pr-3">Template</th>
                        <th className="text-center text-royal-100 text-xs uppercase tracking-wider py-2 px-3">Sent</th>
                        <th className="text-center text-royal-100 text-xs uppercase tracking-wider py-2 px-3">Views</th>
                        <th className="text-center text-royal-100 text-xs uppercase tracking-wider py-2 px-3">Recipients</th>
                        <th className="text-center text-royal-100 text-xs uppercase tracking-wider py-2 pl-3">View Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templatePerformance.map((tp) => {
                        const rate = parseInt(tp.total_recipients) > 0
                          ? Math.round((parseInt(tp.recipients_viewed) / parseInt(tp.total_recipients)) * 100)
                          : 0;
                        return (
                          <tr key={tp.templateid} className="border-b border-royal-700/30">
                            <td className="text-white text-sm py-2.5 pr-3 truncate max-w-[200px]">{tp.templatename}</td>
                            <td className="text-center text-royal-100 text-sm py-2.5 px-3">{tp.times_sent}</td>
                            <td className="text-center text-blue-400 text-sm py-2.5 px-3 font-medium">{tp.total_views}</td>
                            <td className="text-center text-royal-100 text-sm py-2.5 px-3">{tp.total_recipients}</td>
                            <td className="text-center py-2.5 pl-3">
                              <span className={`text-sm font-medium ${rate >= 70 ? 'text-green-400' : rate >= 40 ? 'text-gold' : 'text-amber-400'}`}>
                                {rate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Device Breakdown + Link Performance side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {deviceBreakdown.length > 0 && (
                <div className={`${cardClass} p-5`}>
                  <h2 className="text-white text-base font-semibold mb-4">Device Breakdown</h2>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deviceBreakdown} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                        <XAxis type="number" tick={{ fill: '#9fa8da', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="devicetype" tick={{ fill: '#e8eaf6', fontSize: 12 }} axisLine={false} tickLine={false} width={70} />
                        <Tooltip content={<ChartTooltip />} />
                        <Bar dataKey="count" name="Views" fill="#d4af37" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {canUseLinks && (
                <div className={`${cardClass} p-5`}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-white text-base font-semibold">Profile Links</h2>
                    <Link to="/account/business-profile" className="text-gold text-xs hover:text-gold-light transition-colors">
                      Manage →
                    </Link>
                  </div>
                  {links.length === 0 ? (
                    <p className="text-royal-100 text-sm py-4 text-center">
                      No links yet.{' '}
                      <Link to="/account/business-profile" className="text-gold hover:underline">Add some</Link>
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {links.map((link) => (
                        <div key={link.linkid} className="flex items-center justify-between p-3 bg-royal-900/40 rounded-lg">
                          <div className="min-w-0 flex-1">
                            <p className="text-white text-sm font-medium truncate">{link.name}</p>
                            <p className="text-royal-100 text-xs truncate">{link.url}</p>
                            {link.last_clicked_at && (
                              <p className="text-royal-100 text-xs mt-0.5">Last click: {formatTimeAgo(link.last_clicked_at)}</p>
                            )}
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <p className="text-gold font-bold text-lg">{link.clickcount || 0}</p>
                            <p className="text-royal-100 text-xs">clicks</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Empty state — only when the user TRULY has no cards yet.
                If they have cards but the date range is empty, we show a
                smaller, less alarmist note below instead. */}
            {summary.totalCardsSent === 0 && period === 0 && (
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-8 text-center">
                <p className="text-gold text-lg font-semibold mb-2">No cards sent yet</p>
                <p className="text-royal-600 dark:text-royal-100 text-sm mb-4">Send your first eCard to start seeing analytics!</p>
                <Link to="/moonbeams" className="inline-block px-6 py-2.5 btn-gold text-sm">
                  Browse eCards
                </Link>
              </div>
            )}

            {/* Date-window empty note — user has cards lifetime, but none
                in the picked date range. Less prominent than the
                truly-empty state since the page still has data to show. */}
            {summary.totalCardsSent === 0 && period > 0 && (
              <div className="bg-royal-800/30 border border-royal-700/40 rounded-xl p-4 text-center">
                <p className="text-royal-600 dark:text-royal-100 text-sm">
                  No cards were sent in the {periodLabel}.{' '}
                  <button onClick={() => setPeriod(0)} className="text-gold hover:text-gold-light underline">
                    Show all time
                  </button>
                </p>
              </div>
            )}
          </div>
        )}

        {/* ─── ALL CARDS TAB ─────────────────────────────────── */}
        {activeTab === 'cards' && (
          <div>
            {/* Sort & Filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <select
                value={cardsSort}
                onChange={(e) => setCardsSort(e.target.value)}
                className="px-3 py-2 bg-royal-900 dark:bg-royal-800/60 border border-royal-700 dark:border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold/50"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="most_views">Most views</option>
                <option value="least_views">Least views</option>
              </select>
              <select
                value={cardsFilter}
                onChange={(e) => setCardsFilter(e.target.value)}
                className="px-3 py-2 bg-royal-900 dark:bg-royal-800/60 border border-royal-700 dark:border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold/50"
              >
                <option value="all">All cards</option>
                <option value="unopened">Has unopened recipients</option>
              </select>
            </div>

            {cards.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-royal-600 dark:text-royal-100 text-sm">
                  {cardsFilter === 'unopened'
                    ? `No cards with unopened recipients in the ${periodLabel}.`
                    : period > 0
                      ? `No cards were sent in the ${periodLabel}.`
                      : 'No cards sent yet.'}
                </p>
                {cardsFilter === 'unopened' && (
                  <button onClick={() => setCardsFilter('all')} className="text-gold text-sm mt-2 hover:text-gold-light">
                    Show all cards
                  </button>
                )}
                {cardsFilter !== 'unopened' && period > 0 && (
                  <button onClick={() => setPeriod(0)} className="text-gold text-sm mt-2 hover:text-gold-light">
                    Show all time
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {cards.map((card) => (
                  <CardRow
                    key={card.moonbeamid}
                    card={card}
                    isExpanded={expandedCard === card.moonbeamid}
                    onToggle={() => setExpandedCard(expandedCard === card.moonbeamid ? null : card.moonbeamid)}
                  />
                ))}
              </div>
            )}

            <SimplePagination
              page={cardsPagination.page || 1}
              totalPages={cardsPagination.totalPages || 1}
              onPageChange={setCardsPage}
            />
          </div>
        )}

        {/* ─── SHAREABLE LINKS TAB (NEW) ─────────────────────── */}
        {activeTab === 'links' && (
          <div>
            {/* Description */}
            <div className="mb-4">
              <p className="text-royal-600 dark:text-royal-100 text-sm">
                Links you generated and shared yourself. Views can't be tied to a specific person — they're grouped by the label you gave each link.
              </p>
            </div>

            {/* Stat cards specific to shareable links */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
              <StatCard
                label="Links"
                value={shareableLinksAggregate.totalLinks}
                subtext={`${shareableLinksAggregate.activeLinks} active · ${shareableLinksAggregate.disabledLinks} disabled`}
                color="text-gold"
                icon="🔗"
              />
              <StatCard
                label="Total Views"
                value={shareableLinksAggregate.totalViews}
                subtext="Including repeats"
                color="text-blue-400"
                icon="👁️"
              />
              <StatCard
                label="Active Visitors*"
                value={`~${shareableLinksAggregate.totalUniqueVisitors}`}
                subtext="Unique IPs"
                color="text-gold"
                icon="👥"
              />
              <StatCard
                label="Reactions"
                value={shareableLinksAggregate.totalReactions}
                color="text-pink-400"
                icon="💬"
              />
            </div>

            <p className="text-royal-500 dark:text-royal-100 text-xs italic mb-6">
              *Unique IP approximation. One household may share an IP.
            </p>

            {/* Sort & filter */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <select
                value={shareableLinkSort}
                onChange={(e) => setShareableLinkSort(e.target.value)}
                className="px-3 py-2 bg-royal-900 dark:bg-royal-800/60 border border-royal-700 dark:border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold/50"
              >
                <option value="recent">Most recent activity</option>
                <option value="views">Most views</option>
                <option value="label">By label (A–Z)</option>
              </select>
              <select
                value={shareableLinkFilter}
                onChange={(e) => setShareableLinkFilter(e.target.value)}
                className="px-3 py-2 bg-royal-900 dark:bg-royal-800/60 border border-royal-700 dark:border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold/50"
              >
                <option value="all">All links</option>
                <option value="active">Active only</option>
                <option value="disabled">Disabled only</option>
              </select>
            </div>

            {/* Loading state */}
            {shareableLinksLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
              </div>
            )}

            {/* Empty state */}
            {!shareableLinksLoading && shareableLinksSorted.length === 0 && (
              <div className="bg-gold/10 border border-gold/30 rounded-xl p-8 text-center">
                <p className="text-gold text-lg font-semibold mb-2">
                  {shareableLinksAll.length === 0
                    ? "You haven't created any shareable links yet"
                    : 'No links match this filter'}
                </p>
                {shareableLinksAll.length === 0 ? (
                  <>
                    <p className="text-royal-600 dark:text-royal-100 text-sm mb-4">
                      Next time you send a card, toggle on <strong>Get a link to share</strong> to generate one. You can paste it in text messages, WhatsApp, Facebook, anywhere.
                    </p>
                    <Link to="/moonbeams" className="inline-block px-6 py-2.5 btn-gold text-sm">
                      Browse eCards
                    </Link>
                  </>
                ) : (
                  <button
                    onClick={() => setShareableLinkFilter('all')}
                    className="text-gold text-sm hover:text-gold-light transition-colors"
                  >
                    Show all links
                  </button>
                )}
              </div>
            )}

            {/* Link rows */}
            {!shareableLinksLoading && shareableLinksSorted.length > 0 && (
              <div className="space-y-3">
                {shareableLinksSorted.map(link => (
                  <ShareableLinkAnalyticsRow key={link.linkid} link={link} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── ACTIVITY TAB ──────────────────────────────────── */}
        {activeTab === 'activity' && (
          <div>
            {activity.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-royal-600 dark:text-royal-100 text-sm">
                  {period > 0
                    ? `No activity in the ${periodLabel}.`
                    : 'No recent activity.'}
                </p>
                {period > 0 && (
                  <button onClick={() => setPeriod(0)} className="text-gold text-sm mt-2 hover:text-gold-light">
                    Show all time
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {activity.map((event, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-royal-900 dark:bg-royal-800/30 border border-royal-700 dark:border-royal-700/30 rounded-lg">
                    <EventIcon type={event.eventtype} />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm">
                        <span className="capitalize font-medium">{event.eventtype}</span>
                        {event.recipientname && (
                          <span className="text-royal-100"> by {event.recipientname}</span>
                        )}
                        {!event.recipientname && event.recipient_email && (
                          <span className="text-royal-100"> by {event.recipient_email}</span>
                        )}
                      </p>
                      <p className="text-royal-100 text-xs truncate">
                        {event.templatename}
                        {event.card_recipientname && ` · for ${event.card_recipientname}`}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-royal-100 text-xs">{formatTimeAgo(event.createdat)}</p>
                      {event.devicetype && (
                        <p className="text-royal-100 text-xs">{event.devicetype}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <SimplePagination
              page={activityPagination.page || 1}
              totalPages={activityPagination.totalPages || 1}
              onPageChange={setActivityPage}
            />
          </div>
        )}
      </div>
    </div>
  );
}